import { useState, useEffect, useLayoutEffect } from 'react'
import {
  ArrowRight, ArrowLeft, X, LayoutDashboard, MapPin, TrendingUp,
  BarChart3, Package, Building2, Printer, MessageSquare,
  CheckCircle2, PlayCircle,
} from 'lucide-react'

// Lightweight in-app spotlight tour. Steps optionally name a `target` —
// a `data-tour="<key>"` attribute on an element — and the tour dims the
// rest of the page, draws a highlight ring around the target, and pins
// the tour card to whichever side has room.

const STEPS_REPORTING = [
  { icon: LayoutDashboard, title: 'Welcome to ReportiQ',
    body: `A one-page view of what your sites are spending, with what suppliers, on which products — updated as the orders happen.
Let's take about 90 seconds to walk through it.` },
  { icon: MapPin, title: 'Pick a site (or see them all)', target: 'site-picker',
    body: `The sidebar's Active site dropdown lets you keep the group total ("All sites") or focus on one specific park / hotel / branch.
Every number on the page updates instantly to match.` },
  { icon: TrendingUp, title: 'Your headline numbers', target: 'stat-tiles',
    body: `Five tiles: total spend, active sites, average per site, distinct products, and a year-on-year badge when we have enough history.` },
  { icon: BarChart3, title: 'Month-by-month spend', target: 'monthly-trend',
    body: `The Monthly Spend Trend chart shows every month across your chosen period. Hover a bar to see the exact figure.
This is where seasonal patterns jump out.` },
  { icon: Package, title: 'Where your money is going', target: 'category-and-top',
    body: `Spend by Category splits your total across Toiletries / Cleaning / Welcome Packs etc.
Top Products ranks the individual SKUs by spend so you know exactly what you're buying most of.` },
  { icon: Building2, title: 'How each site compares', target: 'by-site',
    body: `Every site, ranked by spend. Where we have last-year data, a Variance column shows which sites are up and which are down.
Search filters the list for large groups.` },
  { icon: Printer, title: 'Print, save, or share', target: 'export-buttons',
    body: `The Print / save PDF button turns the whole page into a polished report with an executive summary at the top.
Excel gives you the raw numbers for your own analysis.` },
  { icon: MessageSquare, title: 'That\'s it',
    body: `You can re-open this tour any time — the "Take a quick tour" button on the dashboard stays with you.
Any questions, use the details on the Help page.` },
]

const STEPS_ORDERING = [
  { icon: LayoutDashboard, title: 'Welcome to your ordering portal',
    body: `Place orders in a couple of clicks and track what you've spent, when, and where.
This tour takes about 90 seconds — you can skip any time.` },
  { icon: MapPin, title: 'Pick your site', target: 'site-picker',
    body: `If your account covers several sites, the sidebar's Active site dropdown lets you switch between them.
Everything on the page follows.` },
  { icon: Package, title: 'Browse what you can order', target: 'nav-products',
    body: `The Products tab shows exactly what's available to your site, with images, descriptions and prices.
Not everything in our catalogue — just what you've been approved for.` },
  { icon: PlayCircle, title: 'Place an order in four steps', target: 'nav-place',
    body: `Click Place Order in the sidebar. Choose a card (or duplicate a previous order), add quantities,
enter your PO number, and hit Send. A copy hits your inbox straight away.` },
  { icon: BarChart3, title: 'See what you\'ve spent', target: 'nav-reports',
    body: `The ReportiQ button gives you the same reporting your head office sees: monthly trend, top products,
by category, and a printable annual spend report.` },
  { icon: MessageSquare, title: 'Need help?', target: 'nav-help',
    body: `The Help tab has step-by-step instructions and contact details.
We're happy to help — get in touch any time.` },
]

// Padding around the highlighted element (px)
const PAD = 8
// Card dimensions to reserve
const CARD_W = 420
const CARD_MAX_H = 320
const GAP = 16

export default function ReportiqTour({ mode = 'reporting', onClose, brand }) {
  const steps = mode === 'reporting' ? STEPS_REPORTING : STEPS_ORDERING
  const [i, setI] = useState(0)
  const [rect, setRect] = useState(null)     // target bounding rect
  const [cardPos, setCardPos] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' })
  const step = steps[i]
  const isLast = i === steps.length - 1
  const isFirst = i === 0
  const Icon = step.icon

  // Look up the target element by data-tour attribute and compute the
  // highlight rectangle + best card position each time the step changes,
  // and again on scroll / resize.
  useLayoutEffect(() => {
    const compute = () => {
      if (!step.target) { setRect(null); centerCard(); return }
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (!el) { setRect(null); centerCard(); return }
      // Scroll into view once per step
      const r = el.getBoundingClientRect()
      if (r.top < 60 || r.bottom > window.innerHeight - 60) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Recompute shortly after the scroll
        setTimeout(compute, 400)
        return
      }
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 })
      placeCard(r)
    }
    const centerCard = () => setCardPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' })
    const placeCard = (r) => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      // Try below → above → right → left
      const spaceBelow = vh - r.bottom - GAP * 2
      const spaceAbove = r.top - GAP * 2
      const spaceRight = vw - r.right - GAP * 2
      const spaceLeft  = r.left - GAP * 2
      let top, left, transform = ''
      if (spaceBelow >= 220) {
        top  = r.bottom + GAP
        left = Math.min(Math.max(GAP, r.left + r.width / 2 - CARD_W / 2), vw - CARD_W - GAP)
      } else if (spaceAbove >= 220) {
        // Anchor bottom-up so a tall card doesn't go off-screen
        top = Math.max(GAP, r.top - GAP - Math.min(CARD_MAX_H, spaceAbove))
        left = Math.min(Math.max(GAP, r.left + r.width / 2 - CARD_W / 2), vw - CARD_W - GAP)
      } else if (spaceRight >= CARD_W + GAP) {
        top  = Math.min(Math.max(GAP, r.top + r.height / 2 - CARD_MAX_H / 2), vh - CARD_MAX_H - GAP)
        left = r.right + GAP
      } else if (spaceLeft >= CARD_W + GAP) {
        top  = Math.min(Math.max(GAP, r.top + r.height / 2 - CARD_MAX_H / 2), vh - CARD_MAX_H - GAP)
        left = r.left - GAP - CARD_W
      } else {
        // Nowhere it fits — center the modal, dim the whole page
        return centerCard()
      }
      setCardPos({ top: `${top}px`, left: `${left}px`, transform: '' })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [i, step.target])

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && !isLast) setI(i + 1)
      if (e.key === 'ArrowLeft' && !isFirst) setI(i - 1)
      if (e.key === 'Enter' && isLast) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [i, isFirst, isLast, onClose])

  const brandColor = brand?.primary_color_dark || brand?.primary_color || '#0a5842'

  // Overlay: if we have a rect, draw 4 dark strips around it (leaves the
  // target clear). If not, dim the whole viewport.
  const overlay = rect ? (
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: rect.top, background: 'rgba(0,0,0,0.55)', zIndex: 60 }} onClick={onClose} />
      <div style={{ position: 'fixed', top: rect.top, left: 0, width: rect.left, height: rect.height, background: 'rgba(0,0,0,0.55)', zIndex: 60 }} onClick={onClose} />
      <div style={{ position: 'fixed', top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height, background: 'rgba(0,0,0,0.55)', zIndex: 60 }} onClick={onClose} />
      <div style={{ position: 'fixed', top: rect.top + rect.height, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 60 }} onClick={onClose} />
      {/* Highlight ring around the target */}
      <div style={{
        position: 'fixed', top: rect.top, left: rect.left, width: rect.width, height: rect.height,
        borderRadius: 12,
        boxShadow: `0 0 0 3px ${brandColor}, 0 0 0 6px rgba(255,255,255,0.6), 0 0 30px rgba(0,0,0,0.35)`,
        pointerEvents: 'none', zIndex: 61, transition: 'all 0.25s ease',
      }} />
    </>
  ) : (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60 }} onClick={onClose} />
  )

  return (
    <>
      {overlay}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
        style={{ position: 'fixed', width: CARD_W, maxWidth: 'calc(100vw - 24px)', maxHeight: CARD_MAX_H, zIndex: 62, ...cardPos }}
        onClick={e => e.stopPropagation()}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-full transition-all" style={{ width: `${((i + 1) / steps.length) * 100}%`, backgroundColor: brandColor }} />
        </div>

        {/* Header + close */}
        <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: brandColor + '18', color: brandColor }}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Step {i + 1} of {steps.length}</div>
              <h3 className="text-base font-semibold text-gray-900 truncate">{step.title}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0 -mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pb-3">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{step.body}</p>
        </div>

        <div className="px-5 pb-2 flex items-center justify-center gap-1.5">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6' : 'w-1.5 opacity-40 hover:opacity-70'}`}
              style={{ backgroundColor: brandColor }}
              aria-label={`Go to step ${idx + 1}`}
            />
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5">
            Skip
          </button>
          <div className="flex gap-2">
            {!isFirst && (
              <button onClick={() => setI(i - 1)} className="btn-secondary !py-1.5 !px-3">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {isLast ? (
              <button onClick={onClose} className="btn-primary !py-1.5 !px-3">
                <CheckCircle2 className="w-4 h-4" /> Got it
              </button>
            ) : (
              <button onClick={() => setI(i + 1)} className="btn-primary !py-1.5 !px-3">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
